import {
  AnchorButton,
  Button,
  ContextMenuDropdown,
  H3,
  Text,
  Tooltip,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { Link } from 'react-router-dom';

type DesignerActionsProps = {
  projectId: string;
  designName: string;
  saveStatus: string;
  canUndo: boolean;
  canRedo: boolean;
  canSave: boolean;
  saveLabel?: string;
  isSaving: boolean;
  isCreating: boolean;
  isBusy: boolean;
  createDisabledReason: string | null;
  detailsDisabledReason: string | null;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onCreate: () => void;
  onEditDetails: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  deleteDisabledReason: string | null;
  /** Clears every roof, obstacle and panel and arms Draw roof (undoable). */
  onStartOver?: () => void;
  startOverDisabledReason?: string | null;
  /**
   * Arms the whole-model move (M): for when the roof model sits off the
   * imagery, every roof shifts at once.
   */
  onShiftAllRoofs?: () => void;
  shiftAllRoofsDisabledReason?: string | null;
  onBack: () => void;
};

const DesignerActions: FC<DesignerActionsProps> = (props) => {
  const {
    projectId,
    designName,
    saveStatus,
    canUndo,
    canRedo,
    canSave,
    saveLabel = 'Retry save',
    isSaving,
    isCreating,
    isBusy,
    createDisabledReason,
    detailsDisabledReason,
    onUndo,
    onRedo,
    onSave,
    onCreate,
    onEditDetails,
    onDuplicate,
    onDelete,
    deleteDisabledReason,
    onStartOver,
    startOverDisabledReason,
    onShiftAllRoofs,
    shiftAllRoofsDisabledReason,
    onBack,
  } = props;
  const detailsActionDisabledReason =
    detailsDisabledReason ??
    (isBusy ? 'Wait for the current action to finish.' : null);
  const deleteActionDisabledReason =
    deleteDisabledReason ??
    (isBusy ? 'Wait for the current action to finish.' : null);
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="flex w-full min-w-0 items-center gap-3 sm:w-auto sm:flex-1">
        <Tooltip tooltipContent="Back to project">
          <AnchorButton
            as={Link}
            to={`/projects/${projectId}/design`}
            variant="quaternary"
            size="sm"
            className="shrink-0"
            aria-label="Back to project"
            disabled={isBusy}
            onClick={(event) => {
              if (
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              )
                return;
              event.preventDefault();
              onBack();
            }}
          >
            <UntitledIcon name="arrow-left" className="size-4" />
          </AnchorButton>
        </Tooltip>
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <div className="min-w-0 [&>h3]:truncate" title={designName}>
            <H3>{designName}</H3>
          </div>
          <Tooltip
            tooltipContent={detailsActionDisabledReason ?? 'Edit details'}
          >
            <Button
              variant="quaternary"
              size="sm"
              className="shrink-0"
              aria-label="Edit details"
              aria-disabled={!!detailsActionDisabledReason}
              onClick={() => {
                if (!detailsActionDisabledReason) onEditDetails();
              }}
            >
              <UntitledIcon name="edit-05.3" className="size-4" />
            </Button>
          </Tooltip>
        </div>
      </div>
      <div className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-2 sm:w-auto sm:shrink-0">
        <div className="flex shrink-0 items-center">
          <div className="flex items-center">
            <Tooltip
              tooltipContent={canUndo ? 'Undo (⌘/Ctrl Z)' : 'Nothing to undo'}
            >
              <Button
                variant="quaternary"
                size="sm"
                aria-label="Undo"
                aria-disabled={!canUndo || isBusy}
                onClick={() => {
                  if (canUndo && !isBusy) onUndo();
                }}
              >
                <UntitledIcon
                  name="corner-up-right.3"
                  className="size-4 -scale-x-100"
                />
              </Button>
            </Tooltip>
            <Tooltip
              tooltipContent={
                canRedo ? 'Redo (⌘/Ctrl Shift Z)' : 'Nothing to redo'
              }
            >
              <Button
                variant="quaternary"
                size="sm"
                aria-label="Redo"
                aria-disabled={!canRedo || isBusy}
                onClick={() => {
                  if (canRedo && !isBusy) onRedo();
                }}
              >
                <UntitledIcon name="corner-up-right.3" className="size-4" />
              </Button>
            </Tooltip>
          </div>
          <div className="ml-2 min-w-20 whitespace-nowrap" role="status">
            <Text level="supporting">{isSaving ? 'Saving…' : saveStatus}</Text>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ContextMenuDropdown
            variant="quaternary"
            size="sm"
            label="Design actions"
            menuItems={[
              ...(onStartOver
                ? [
                    {
                      // The menu does not show disabledReasons, so an
                      // unavailable Start over carries its reason as text.
                      label: startOverDisabledReason ? (
                        <span className="flex flex-col items-start">
                          <span>Start over</span>
                          <span className="text-xs text-gray-500">
                            {startOverDisabledReason}
                          </span>
                        </span>
                      ) : (
                        'Start over'
                      ),
                      disabled: !!startOverDisabledReason || isBusy,
                      disabledReasons: startOverDisabledReason
                        ? [startOverDisabledReason]
                        : undefined,
                      onClick: onStartOver,
                    },
                  ]
                : []),
              ...(onShiftAllRoofs
                ? [
                    {
                      label: shiftAllRoofsDisabledReason ? (
                        <span className="flex flex-col items-start">
                          <span>Shift all roofs together</span>
                          <span className="text-xs text-gray-500">
                            {shiftAllRoofsDisabledReason}
                          </span>
                        </span>
                      ) : (
                        'Shift all roofs together'
                      ),
                      disabled: !!shiftAllRoofsDisabledReason || isBusy,
                      disabledReasons: shiftAllRoofsDisabledReason
                        ? [shiftAllRoofsDisabledReason]
                        : undefined,
                      onClick: onShiftAllRoofs,
                    },
                  ]
                : []),
              {
                label: 'Duplicate design',
                disabled: !!detailsActionDisabledReason,
                disabledReasons: detailsActionDisabledReason
                  ? [detailsActionDisabledReason]
                  : undefined,
                onClick: onDuplicate,
              },
              {
                label: 'Delete design',
                disabled: !!deleteActionDisabledReason,
                disabledReasons: deleteActionDisabledReason
                  ? [deleteActionDisabledReason]
                  : undefined,
                onClick: onDelete,
              },
            ]}
          />
          {canSave && (
            <Button
              variant="secondary"
              size="sm"
              disabled={isBusy}
              isProcessing={isSaving}
              onClick={onSave}
            >
              {saveLabel}
            </Button>
          )}
          <Tooltip
            tooltipContent={createDisabledReason ?? ''}
            enabled={!!createDisabledReason}
          >
            <Button
              size="sm"
              aria-disabled={!!createDisabledReason || isBusy}
              isProcessing={isCreating}
              onClick={() => {
                if (!createDisabledReason && !isBusy) onCreate();
              }}
            >
              Create estimate
            </Button>
          </Tooltip>
        </div>
      </div>
      {isCreating && (
        <div className="basis-full" role="status">
          <Text level="supporting">
            Building your estimate. Its layout image will follow in the
            background.
          </Text>
        </div>
      )}
    </div>
  );
};

export default DesignerActions;
